import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutData } from '@/mocks/about';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于蓝莓视频',
  description: aboutData.brandIntro,
  url: 'https://www.lanmeishipin.com/about',
  inLanguage: 'zh-CN',
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: aboutData.faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

export default function AboutPage() {
  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-10">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-[1200px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            <section className="mb-12">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-6">
                关于蓝莓视频
              </h1>
              <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70">
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {aboutData.brandIntro}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                  <div className="bg-primary-50 rounded-xl p-5">
                    <h3 className="text-base font-semibold text-primary-700 mb-2">
                      <i className="ri-focus-3-line mr-2"></i>我们的使命
                    </h3>
                    <p className="text-sm text-primary-600">{aboutData.mission}</p>
                  </div>
                  <div className="bg-accent-50 rounded-xl p-5">
                    <h3 className="text-base font-semibold text-accent-700 mb-2">
                      <i className="ri-eye-line mr-2"></i>我们的愿景
                    </h3>
                    <p className="text-sm text-accent-600">{aboutData.vision}</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-6 top-0 bottom-0 w-px bg-background-300"></div>
                <div className="space-y-6">
                  {aboutData.milestones.map((m, i) => (
                    <div key={i} className="relative pl-12 md:pl-16">
                      <div className="absolute left-2 md:left-4 top-1.5 w-5 h-5 flex items-center justify-center rounded-full bg-primary-500 text-white">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                      <div className="bg-background-50 rounded-xl p-4 md:p-5 border border-background-200/70">
                        <span className="text-xs text-primary-500 font-semibold">{m.year}</span>
                        <p className="text-sm text-foreground-700 mt-1">{m.event}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                {aboutData.testimonials.map((t, i) => (
                  <div key={i} className="bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/70">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, j) => (
                        <i key={j} className={`ri-star-fill text-sm ${j < t.rating ? 'text-accent-500' : 'text-background-300'}`}></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      &ldquo;{t.content}&rdquo;
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 text-sm font-semibold">
                        {t.name[0]}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                        <p className="text-xs text-foreground-500">{t.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                常见问题
              </h2>
              <div className="space-y-3">
                {aboutData.faqs.map((faq, i) => (
                  <details key={i} className="group bg-background-50 rounded-xl border border-background-200/70">
                    <summary className="flex items-center justify-between p-5 cursor-pointer">
                      <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">{faq.question}</span>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                联系我们
              </h2>
              <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">邮箱</h3>
                      <p className="text-sm text-foreground-600">{aboutData.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">电话</h3>
                      <p className="text-sm text-foreground-600">{aboutData.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">地址</h3>
                      <p className="text-sm text-foreground-600">{aboutData.contact.address}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-6 pt-6 border-t border-background-200">
                  <span className="text-sm text-foreground-500">关注我们：</span>
                  <a href="#" className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-200 hover:bg-primary-500 hover:text-white transition-colors text-foreground-600 cursor-pointer" aria-label="微信">
                    <i className="ri-wechat-line"></i>
                  </a>
                  <a href="#" className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-200 hover:bg-primary-500 hover:text-white transition-colors text-foreground-600 cursor-pointer" aria-label="微博">
                    <i className="ri-weibo-line"></i>
                  </a>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}